import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, THEMES } from '../contexts/ThemeContext';

// ── Constants ────────────────────────────────────────────────
const LANGUAGES = ['Dutch', 'English', 'Both'];
const TONES = ['Professional', 'Warm & Friendly', 'Luxury'];

const DOC_CATEGORIES = [
  { key: 'algemeen',   label: 'Algemeen' },
  { key: 'faq',        label: 'FAQ Antwoord' },
  { key: 'proposal',   label: 'Reisvoorstellen' },
  { key: 'newsletter', label: 'Nieuwsbrieven' },
  { key: 'chatbot',    label: 'Chatbot' },
];

const FEATURE_META = {
  faq_reply:  { label: 'FAQ Antwoord',  desc: 'AI-antwoorden op klantvragen' },
  proposal:   { label: 'Reisvoorstel', desc: 'Reisvoorstellen genereren' },
  newsletter: { label: 'Nieuwsbrief',  desc: 'Nieuwsbrieven genereren' },
  ai_chatbot: { label: 'AI Chatbot',   desc: 'Gedrag van de AI-assistent' },
};

const PLACEHOLDER_KB = `Wij zijn gespecialiseerd in vakanties naar de Caraïben en de Middellandse Zee.\n\nVeelgestelde vragen:\n- Annulering: Gratis tot 30 dagen voor vertrek, daarna 50% annuleringskosten\n- Reisverzekering: Wij werken samen met Europcar Assurance voor uitgebreide dekking\n- Paspoort: Geldig tot minimaal 6 maanden na terugreis\n- All-inclusive: Inclusief maaltijden, drankjes en bepaalde activiteiten op het resort\n- Visum: Wij adviseren over visumvereisten per bestemming`;

// ── Sub-tabs ─────────────────────────────────────────────────
const SETTINGS_TABS = [
  { id: 'profiel',    label: 'Voorkeuren' },
  { id: 'ai',         label: 'AI Instructies' },
  { id: 'fotos',      label: 'Foto Bibliotheek' },
  { id: 'email',      label: 'E-mail Integratie' },
  { id: 'kennisbank', label: 'Kennisbank AI' },
];

const CHATBOT_MODES = [
  { value: 'general_only',            label: 'Alleen algemeen', desc: 'Geen kennisbank — Claude antwoordt vanuit algemene kennis.' },
  { value: 'supplier_knowledge_only', label: 'Alleen leverancierskennis', desc: 'Antwoordt alleen vanuit kennisbank. Bij geen match: "Geen leveranciersinformatie gevonden."' },
  { value: 'hybrid',                  label: 'Hybride (aanbevolen)', desc: 'Kennisbank bij medium/hoge confidence, anders algemene kennis als fallback.' },
];

// ════════════════════════════════════════════════════════
// Kennisbank AI Tab
// ════════════════════════════════════════════════════════
function KennisbankTab({ form, setForm }) {
  return (
    <div className="max-w-2xl space-y-6">
      {/* Chatbot answer mode */}
      <div className="pro-card p-5">
        <h2 className="font-semibold text-gray-900 mb-1">Chatbot antwoordmodus</h2>
        <p className="text-xs text-gray-500 mb-4">Bepaal hoe de AI-chatbot antwoorden samenstelt wanneer kennisitems aanwezig zijn.</p>
        <div className="space-y-2.5">
          {CHATBOT_MODES.map(m => (
            <label key={m.value}
              className={`flex items-start gap-3 p-3.5 rounded-lg border cursor-pointer transition-all ${form.chatbot_answer_mode === m.value ? 'border-blue-300 bg-blue-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="chatbot_answer_mode"
                value={m.value}
                checked={form.chatbot_answer_mode === m.value}
                onChange={() => setForm(f => ({ ...f, chatbot_answer_mode: m.value }))}
                className="mt-0.5 accent-blue-600 flex-shrink-0"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{m.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* FAQ + General fallback */}
      <div className="pro-card p-5 space-y-4">
        <h2 className="font-semibold text-gray-900">FAQ &amp; Fallback</h2>

        {/* faq_use_knowledge */}
        <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100">
          <div>
            <p className="text-sm font-medium text-gray-900">Kennisbank gebruiken bij FAQ-antwoorden</p>
            <p className="text-xs text-gray-500 mt-0.5">Haalt relevante kennisitems op bij het genereren van e-mailantwoorden op klantvragen.</p>
          </div>
          <label className="relative flex-shrink-0 mt-0.5 cursor-pointer">
            <input type="checkbox" className="sr-only peer"
              checked={!!form.faq_use_knowledge}
              onChange={e => setForm(f => ({ ...f, faq_use_knowledge: e.target.checked }))} />
            <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors" />
            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
          </label>
        </div>

        {/* allow_general_fallback */}
        <div className="flex items-start justify-between gap-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Algemene kennis als fallback toestaan</p>
            <p className="text-xs text-gray-500 mt-0.5">Bij hybride modus: als geen relevante kennisitems gevonden worden, mag Claude ook algemene reiskennis gebruiken.</p>
          </div>
          <label className="relative flex-shrink-0 mt-0.5 cursor-pointer">
            <input type="checkbox" className="sr-only peer"
              checked={form.allow_general_fallback !== false}
              onChange={e => setForm(f => ({ ...f, allow_general_fallback: e.target.checked }))} />
            <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors" />
            <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
          </label>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Beheer kennisitems en importeer URLs via de{' '}
        <a href="/knowledge" className="text-blue-500 hover:underline">Kennisbank-pagina</a>.
      </p>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// AI Instructies Tab
// ════════════════════════════════════════════════════════
function AIInstructiesTab({ authFetch }) {
  const [features, setFeatures] = useState([]);
  const [instructions, setInstructions] = useState({});
  const [activeFeature, setActiveFeature] = useState('faq_reply');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [activeDocCategory, setActiveDocCategory] = useState('algemeen');
  const docInputRef = useRef(null);

  useEffect(() => { loadInstructions(); loadDocs(); }, []);

  const loadInstructions = async () => {
    const res = await authFetch('/api/ai-instructions');
    const data = await res.json();
    setFeatures(data.features || []);
    setInstructions(data.instructions || {});
  };

  const loadDocs = async () => {
    const res = await authFetch('/api/documents');
    const data = await res.json();
    setDocs(Array.isArray(data) ? data : []);
  };

  const current = instructions[activeFeature] || {};

  const handleSave = async () => {
    setSaving(true); setSaveMsg('');
    await authFetch(`/api/ai-instructions/${activeFeature}`, {
      method: 'POST',
      body: JSON.stringify({
        custom_system_prompt: current.custom_system_prompt || '',
        example_data: current.example_data || '',
        is_active: current.is_active !== false,
      }),
    });
    setSaveMsg('Opgeslagen!');
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 3000);
    loadInstructions();
  };

  const handleReset = async () => {
    if (!window.confirm('Weet u zeker dat u de instructies wilt resetten?')) return;
    await authFetch(`/api/ai-instructions/${activeFeature}`, { method: 'DELETE' });
    loadInstructions();
  };

  const updateCurrent = (field, value) => {
    setInstructions(prev => ({ ...prev, [activeFeature]: { ...prev[activeFeature], [field]: value } }));
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true); setUploadMsg('');
    const fd = new FormData();
    files.forEach(f => fd.append('documents', f));
    fd.append('category', activeDocCategory);
    try {
      const token = localStorage.getItem('tripdash_token');
      const res = await fetch('/api/documents/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (data.success) { setUploadMsg(`${data.uploaded.length} document(en) geüpload!`); loadDocs(); }
    } catch { setUploadMsg('Upload mislukt'); }
    setUploading(false);
    setTimeout(() => setUploadMsg(''), 5000);
    e.target.value = '';
  };

  const handleDeleteDoc = async (id) => {
    await authFetch(`/api/documents/${id}`, { method: 'DELETE' });
    loadDocs();
  };

  const fmt = (bytes) => bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  return (
    <div className="space-y-6">
      {/* Training docs */}
      <div className="pro-card p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-semibold text-gray-900">Trainingsmateriaal</h2>
            <p className="text-gray-500 text-xs mt-1">Upload documenten per type. De AI gebruikt ze als leidende referentie voor stijl, taal en opmaak.</p>
          </div>
          <div>
            <button onClick={() => docInputRef.current?.click()} disabled={uploading} className="btn-primary text-sm">
              {uploading ? <span className="spinner" /> : null} {uploading ? 'Uploaden...' : 'Document uploaden'}
            </button>
            <input ref={docInputRef} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden" onChange={handleDocUpload} />
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-0 flex-wrap mb-4 pb-0 border-b border-gray-200">
          {DOC_CATEGORIES.map(cat => {
            const count = docs.filter(d => (d.category || 'algemeen') === cat.key).length;
            return (
              <button key={cat.key} onClick={() => setActiveDocCategory(cat.key)}
                className={`pro-tab flex items-center gap-1.5 ${activeDocCategory === cat.key ? 'active' : ''}`}>
                {cat.label}
                {count > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    activeDocCategory === cat.key ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-gray-400 mb-3 mt-3">
          Documenten voor <strong>{DOC_CATEGORIES.find(c => c.key === activeDocCategory)?.label}</strong> — worden gebruikt als leidende referentie bij het genereren van dit type content.
        </p>

        {uploadMsg && (
          <div className="mb-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm px-3 py-2">{uploadMsg}</div>
        )}

        {(() => {
          const filtered = docs.filter(d => (d.category || 'algemeen') === activeDocCategory);
          return filtered.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <p className="text-gray-500 text-sm">Geen documenten voor {DOC_CATEGORIES.find(c => c.key === activeDocCategory)?.label}</p>
              <p className="text-gray-400 text-xs mt-1">Ondersteunde formaten: PDF, DOCX, TXT, Markdown</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-xs font-semibold text-blue-600">
                      {doc.file_type?.includes('pdf') ? 'PDF' : doc.file_type?.includes('word') ? 'DOC' : 'TXT'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                      <p className="text-xs text-gray-400">{fmt(doc.file_size || 0)} · {new Date(doc.created_at).toLocaleDateString('nl-NL')}</p>
                    </div>
                  </div>
                  <button onClick={() => handleDeleteDoc(doc.id)} className="text-red-400 hover:text-red-600 text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors">
                    Verwijderen
                  </button>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Per-feature instructions */}
      <div className="pro-card p-5">
        <h2 className="font-semibold text-gray-900 mb-4">AI-instructies per onderdeel</h2>
        <div className="flex gap-0 flex-wrap mb-6 border-b border-gray-200">
          {features.map(f => {
            const meta = FEATURE_META[f.key] || {};
            const hasCustom = instructions[f.key]?.custom_system_prompt;
            return (
              <button key={f.key} onClick={() => setActiveFeature(f.key)}
                className={`pro-tab flex items-center gap-2 ${activeFeature === f.key ? 'active' : ''}`}>
                {meta.label || f.label}
                {hasCustom && <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-1" />}
              </button>
            );
          })}
        </div>
        {activeFeature && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{FEATURE_META[activeFeature]?.label}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{FEATURE_META[activeFeature]?.desc}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">Actief</span>
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" checked={current.is_active !== false} onChange={e => updateCurrent('is_active', e.target.checked)} />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
              </label>
            </div>
            <div>
              <label className="label">Aangepaste systeem-instructie</label>
              <p className="text-xs text-gray-400 mb-2">Schrijf hier extra instructies voor de AI. Dit wordt vóór de standaard-instructie geplaatst.</p>
              <textarea className="input font-mono text-xs min-h-40 resize-y"
                placeholder="Bijv.: Schrijf altijd in een warme, persoonlijke stijl. Gebruik altijd 'u' als aanspreekwijze..."
                value={current.custom_system_prompt || ''}
                onChange={e => updateCurrent('custom_system_prompt', e.target.value)} />
            </div>
            <div>
              <label className="label">Voorbeelddata / Schrijfstijl voorbeelden</label>
              <p className="text-xs text-gray-400 mb-2">Plak hier voorbeelden van uw eigen teksten. De AI gebruikt deze als stijlreferentie.</p>
              <textarea className="input text-xs min-h-48 resize-y"
                placeholder="Plak hier voorbeelden van uw eigen voorstellen, brieven of antwoorden..."
                value={current.example_data || ''}
                onChange={e => updateCurrent('example_data', e.target.value)} />
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                {saving ? <span className="spinner" /> : null}{saving ? 'Opslaan...' : 'Instructies opslaan'}
              </button>
              <button onClick={handleReset} className="btn-secondary text-sm text-red-600 hover:bg-red-50">Reset naar standaard</button>
              {saveMsg && (
                <span className="bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm px-3 py-2">{saveMsg}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════
// Foto Bibliotheek Tab
// ════════════════════════════════════════════════════════
function FotoBibliotheekTab({ authFetch }) {
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ continents: {}, countries: {} });
  const [addingTo, setAddingTo] = useState(null);
  const [addForm, setAddForm] = useState({ continent: '', country: '', area: '', title: '', photo_url: '' });
  const [addMode, setAddMode] = useState('url');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const fileRef = useRef(null);
  const [newArea, setNewArea] = useState({ continent: '', country: '', area: '' });
  const [showNewArea, setShowNewArea] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const res = await authFetch('/api/destination-photos'); const data = await res.json(); setHierarchy(data.hierarchy || {}); } catch {}
    setLoading(false);
  };

  const toggleContinent = (c) => setExpanded(e => ({ ...e, continents: { ...e.continents, [c]: !e.continents[c] } }));
  const toggleCountry = (k) => setExpanded(e => ({ ...e, countries: { ...e.countries, [k]: !e.countries[k] } }));

  const openAdd = (continent, country, area) => {
    setAddingTo({ continent, country, area });
    setAddForm({ continent, country, area, title: area, photo_url: '' });
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!addForm.continent || !addForm.country || !addForm.area) return;
    setSaving(true); setSaveMsg('');
    try {
      const tok = localStorage.getItem('tripdash_token');
      const fd = new FormData();
      fd.append('continent', addForm.continent); fd.append('country', addForm.country);
      fd.append('area', addForm.area); fd.append('title', addForm.title || addForm.area);
      if (addMode === 'url') { fd.append('photo_url', addForm.photo_url); }
      else if (fileRef.current?.files?.[0]) { fd.append('photo', fileRef.current.files[0]); }
      const res = await fetch('/api/destination-photos', { method: 'POST', headers: { Authorization: `Bearer ${tok}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSaveMsg('Foto opgeslagen!'); setAddingTo(null); load();
    } catch (e) { setSaveMsg('Fout: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => { await authFetch(`/api/destination-photos/${id}`, { method: 'DELETE' }); load(); };

  const handleAddNewArea = () => {
    if (!newArea.continent || !newArea.country || !newArea.area) return;
    openAdd(newArea.continent, newArea.country, newArea.area);
    setShowNewArea(null);
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="spinner spinner-blue" style={{ width: 32, height: 32 }} /></div>;

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
        <p className="font-semibold mb-1">Hoe werkt de foto bibliotheek?</p>
        <p className="text-xs text-blue-700">Beheer hier bestemmingsfoto's per continent, land en regio. Deze foto's gebruikt de AI automatisch als voorkeursfoto bij het genereren van reisvoorstellen.</p>
      </div>

      {saveMsg && !addingTo && (
        <div className={`px-3 py-2 rounded-lg text-sm border ${saveMsg.startsWith('Fout') ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{saveMsg}</div>
      )}

      <div className="pro-card p-5">
        {Object.keys(hierarchy).length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm font-medium">Nog geen foto's toegevoegd</p>
            <p className="text-gray-400 text-xs mt-1">Voeg hieronder uw eerste bestemming en foto toe</p>
          </div>
        ) : (
          Object.entries(hierarchy).map(([continent, countries]) => (
            <div key={continent} className="mb-2">
              <button onClick={() => toggleContinent(continent)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-bold text-white transition-all"
                style={{ background: 'linear-gradient(135deg, #1a6bab, #0d4a7a)' }}>
                <span className="flex-1">{continent}</span>
                <svg className={`w-4 h-4 transition-transform ${expanded.continents[continent] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expanded.continents[continent] && (
                <div className="ml-4 mt-2 space-y-2">
                  {Object.entries(countries).map(([country, areas]) => {
                    const countryKey = `${continent}-${country}`;
                    const totalPhotos = Object.values(areas).reduce((s, ps) => s + ps.length, 0);
                    return (
                      <div key={country}>
                        <button onClick={() => toggleCountry(countryKey)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-all">
                          <span className="font-semibold text-gray-800 flex-1">{country}</span>
                          <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded-full border border-gray-200">{totalPhotos} foto's</span>
                          <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded.countries[countryKey] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {expanded.countries[countryKey] && (
                          <div className="ml-4 mt-2 space-y-3">
                            {Object.entries(areas).map(([area, photos]) => (
                              <div key={area} className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100">
                                  <span className="font-medium text-gray-700 text-sm">{area}</span>
                                  <button onClick={() => openAdd(continent, country, area)}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-lg font-bold hover:opacity-80"
                                    style={{ backgroundColor: 'var(--brand)' }}>+</button>
                                </div>
                                <div className="p-3 flex gap-2 flex-wrap bg-gray-50">
                                  {photos.map(photo => (
                                    <div key={photo.id} className="relative group">
                                      <img src={photo.photo_url} alt={photo.title}
                                        className="w-24 object-cover rounded-lg border border-gray-200" style={{ height: 72 }}
                                        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=200&q=60'; }} />
                                      <button onClick={() => handleDelete(photo.id)}
                                        className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">×</button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {showNewArea === countryKey ? (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                                <p className="text-xs font-semibold text-blue-700 mb-2">Nieuwe regio toevoegen in {country}</p>
                                <input className="input text-sm mb-2" placeholder="Naam van de regio"
                                  value={newArea.area} onChange={e => setNewArea({ continent, country, area: e.target.value })} />
                                <div className="flex gap-2">
                                  <button onClick={handleAddNewArea} className="btn-primary text-xs py-1.5">Toevoegen + foto</button>
                                  <button onClick={() => setShowNewArea(null)} className="btn-secondary text-xs py-1.5">Annuleren</button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => { setShowNewArea(countryKey); setNewArea({ continent, country, area: '' }); }}
                                className="w-full py-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all text-sm">
                                + Nieuwe regio toevoegen
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add photo modal */}
      {addingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Foto toevoegen — {addingTo.area}</h3>
              <button onClick={() => setAddingTo(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">{addingTo.continent} › {addingTo.country} › {addingTo.area}</p>
            <div className="mb-4">
              <label className="label">Fototitel</label>
              <input className="input" placeholder={addingTo.area} value={addForm.title} onChange={e => setAddForm({ ...addForm, title: e.target.value })} />
            </div>
            <div className="flex gap-2 mb-4">
              {['url', 'file'].map(m => (
                <button key={m} onClick={() => setAddMode(m)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${addMode === m ? 'text-white border-transparent' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                  style={addMode === m ? { backgroundColor: 'var(--brand)' } : {}}>
                  {m === 'url' ? 'URL' : 'Bestand'}
                </button>
              ))}
            </div>
            {addMode === 'url' ? (
              <div className="mb-4">
                <label className="label">Foto URL</label>
                <input className="input" placeholder="https://images.unsplash.com/..." value={addForm.photo_url}
                  onChange={e => setAddForm({ ...addForm, photo_url: e.target.value })} />
                {addForm.photo_url && <img src={addForm.photo_url} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-lg border border-gray-200" onError={e => { e.target.style.display='none'; }} />}
              </div>
            ) : (
              <div className="mb-4">
                <label className="label">Afbeeldingsbestand</label>
                <input ref={fileRef} type="file" accept="image/*" className="input" />
              </div>
            )}
            {saveMsg && (
              <p className={`text-sm mb-3 ${saveMsg.startsWith('Fout') ? 'text-red-600' : 'text-green-600'}`}>{saveMsg}</p>
            )}
            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? <span className="spinner" /> : null}{saving ? 'Opslaan...' : 'Foto opslaan'}
              </button>
              <button onClick={() => setAddingTo(null)} className="btn-secondary">Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════
// Main Settings Component
// ════════════════════════════════════════════════════════
export default function Settings() {
  const { authFetch, user } = useAuth();
  const { theme: activeTheme, setTheme, customTheme, setCustomTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profiel');
  const [form, setForm] = useState({
    agency_name: '', agent_name: '', agent_slug: '', agent_bio: '',
    website_url: '', phone: '', office_address: '',
    default_language: 'Dutch', default_tone: 'Warm & Friendly', email_signoff: '',
    faq_knowledge_base: '',
    imap_host: '', imap_port: 993, imap_user: '', imap_password: '',
    imap_folder: 'FAQ', imap_enabled: false,
    bas_number: '', smtp_host: '', smtp_port: 587, reisbescheiden_folder: 'Reisbescheiden', history_folder: 'History',
    appointment_confirmation_template: '',
    appointment_reminder_template: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Custom theme editor state
  const [customDesc, setCustomDesc] = useState('');
  const [customDynamic, setCustomDynamic] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [previewTheme, setPreviewTheme] = useState(null); // generated but not yet saved

  useEffect(() => {
    authFetch('/api/settings').then(r => r.json()).then(data => {
      setForm({
        agency_name: data.agency_name || '', agent_name: data.agent_name || '',
        agent_slug: data.agent_slug || '', agent_bio: data.agent_bio || '',
        website_url: data.website_url || '', phone: data.phone || '',
        office_address: data.office_address || '', default_language: data.default_language || 'Dutch',
        default_tone: data.default_tone || 'Warm & Friendly', email_signoff: data.email_signoff || '',
        auto_email_enabled: !!data.auto_email_enabled,
        faq_knowledge_base: data.faq_knowledge_base || '', imap_host: data.imap_host || '',
        imap_port: data.imap_port || 993, imap_user: data.imap_user || '',
        imap_password: '', imap_folder: data.imap_folder || 'FAQ',
        imap_enabled: data.imap_enabled || false,
        bas_number: data.bas_number || '',
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        reisbescheiden_folder: data.reisbescheiden_folder || 'Reisbescheiden',
        history_folder: data.history_folder || 'History',
        appointment_confirmation_template: data.appointment_confirmation_template || '',
        appointment_reminder_template: data.appointment_reminder_template || '',
        // Knowledge integration
        chatbot_answer_mode   : data.chatbot_answer_mode    || 'general_only',
        faq_use_knowledge     : !!data.faq_use_knowledge,
        allow_general_fallback: data.allow_general_fallback !== false,
      });
      // Restore custom theme from server if present and not already in localStorage
      if (data.custom_theme && !customTheme) {
        setCustomTheme(data.custom_theme);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      const res = await authFetch('/api/settings', {
        method: 'POST',
        body: JSON.stringify({ ...form, custom_theme: customTheme }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Opslaan mislukt');
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleGenerateTheme = async () => {
    if (!customDesc.trim()) return;
    setGenerating(true); setGenError(''); setPreviewTheme(null);
    try {
      const res = await authFetch('/api/settings/generate-theme', {
        method: 'POST',
        body: JSON.stringify({ description: customDesc, dynamic: customDynamic }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Genereren mislukt');
      setPreviewTheme(data.theme);
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyCustomTheme = () => {
    if (!previewTheme) return;
    setCustomTheme(previewTheme);
    setTheme('custom');
    setPreviewTheme(null);
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
    <div className="p-8 page-enter" style={{ flex: 1, overflowY: 'auto' }}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Instellingen</h1>
        <p className="text-gray-500 text-sm mt-1">Beheer uw profiel, AI-instructies, foto's en integraties</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 mb-6 border-b border-gray-200">
        {SETTINGS_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pro-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm px-3 py-2">{error}</div>
      )}

      {/* ── Profiel & Voorkeuren ─────────────── */}
      {activeTab === 'profiel' && (
        <div className="max-w-3xl space-y-6">
          {/* AI Preferences */}
          <section className="pro-card p-5">
            <h2 className="font-semibold text-gray-900 mb-4">AI-voorkeuren</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Standaard taal</label>
                <select className="input" value={form.default_language} onChange={e => setForm({ ...form, default_language: e.target.value })}>
                  {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Standaard toon</label>
                <select className="input" value={form.default_tone} onChange={e => setForm({ ...form, default_tone: e.target.value })}>
                  {TONES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label">Standaard e-mail afsluiting</label>
                <input className="input" placeholder="Met vriendelijke groet, Jan van Reizen aan Zee"
                  value={form.email_signoff} onChange={e => setForm({ ...form, email_signoff: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Automatisering */}
          <section className="pro-card p-5">
            <h2 className="font-semibold text-gray-900 mb-1">Automatisering</h2>
            <p className="text-xs text-gray-500 mb-4">Laat het systeem dagelijks automatisch e-mailconcepten genereren op basis van boekingen.</p>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Automatische klant-e-mails</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Genereert dagelijks pre-trip concepten (5 dagen voor vertrek) en post-trip concepten (3 dagen na terugkomst).
                  Concepten zijn zichtbaar onder <strong>FAQ → Automatische mail</strong> voor review, bewerking en versturen.
                </p>
              </div>
              <label className="relative flex-shrink-0 mt-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!!form.auto_email_enabled}
                  onChange={e => setForm(f => ({ ...f, auto_email_enabled: e.target.checked }))}
                />
                <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
              </label>
            </div>
          </section>

          {/* FAQ Knowledge Base */}
          <section className="pro-card p-5">
            <h2 className="font-semibold text-gray-900 mb-2">FAQ kennisbank</h2>
            <p className="text-sm text-gray-500 mb-4">Wordt gebruikt als context bij het genereren van AI-antwoorden op klantvragen.</p>
            <textarea className="input min-h-64 text-sm font-mono leading-relaxed" placeholder={PLACEHOLDER_KB}
              value={form.faq_knowledge_base} onChange={e => setForm({ ...form, faq_knowledge_base: e.target.value })} />
          </section>

          {/* Theme Picker */}
          <section className="pro-card p-5">
            <h2 className="font-semibold text-gray-900 mb-2">Platformthema</h2>
            <p className="text-sm text-gray-500 mb-5">Kies een kleurthema of beschrijf uw eigen stijl. De wijziging is direct zichtbaar.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {THEMES.map(t => {
                const isActive = activeTheme === t.id;
                const THEME_GRADIENTS = {
                  ocean:    'linear-gradient(160deg, #0d3472 0%, #1a6bab 45%, #1a8fc1 80%, #0d3472 100%), radial-gradient(ellipse at 20% 50%, rgba(26,143,193,0.5) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)',
                  forest:   'linear-gradient(160deg, #1a3d1a 0%, #2d6a2d 40%, #1e4d1e 80%, #0f2a0f 100%), radial-gradient(ellipse at 70% 15%, rgba(255,215,80,0.35) 0%, transparent 45%), radial-gradient(ellipse at 30% 80%, rgba(45,106,45,0.6) 0%, transparent 55%)',
                  sunset:   'linear-gradient(160deg, #7a0a0a 0%, #c0392b 30%, #e67e22 60%, #8b1a1a 100%), radial-gradient(ellipse at 50% 0%, rgba(255,160,50,0.6) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(180,30,30,0.5) 0%, transparent 50%)',
                  beach:    'linear-gradient(160deg, #1565b8 0%, #4a9fd4 40%, #87ceeb 65%, #c8a96e 100%), radial-gradient(ellipse at 60% 20%, rgba(255,255,200,0.25) 0%, transparent 45%), radial-gradient(ellipse at 20% 90%, rgba(200,169,110,0.4) 0%, transparent 45%)',
                  northern: 'linear-gradient(160deg, #0d0d2b 0%, #1a0a3d 30%, #0d2b1a 60%, #0a1a2b 100%), radial-gradient(ellipse at 30% 40%, rgba(100,220,130,0.35) 0%, transparent 50%), radial-gradient(ellipse at 70% 30%, rgba(140,60,220,0.4) 0%, transparent 50%), radial-gradient(ellipse at 50% 70%, rgba(60,160,220,0.25) 0%, transparent 45%)',
                  custom:   customTheme?.sidebar_bg || 'linear-gradient(160deg, #374151 0%, #6b7280 50%, #374151 100%)',
                };
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      if (t.id === 'custom' && !customTheme) return; // must generate first
                      setTheme(t.id);
                    }}
                    disabled={t.id === 'custom' && !customTheme}
                    className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-200 text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                      isActive ? 'scale-105 shadow-lg' : 'border-transparent hover:scale-102'
                    }`}
                    style={{ borderColor: isActive ? 'var(--brand)' : 'transparent' }}
                  >
                    <div className="h-20 w-full" style={{ background: THEME_GRADIENTS[t.id] }}>
                      <div className="w-full h-full flex items-start justify-between p-2 relative">
                        {isActive && (
                          <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center shadow">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                        <span className="text-lg ml-auto opacity-90">{t.emoji}</span>
                      </div>
                    </div>
                    <div className="p-2 bg-white">
                      <p className="font-semibold text-gray-900 text-xs">{t.name}</p>
                      <p className="text-gray-400 text-xs leading-tight mt-0.5 truncate">{t.id === 'custom' && !customTheme ? 'Nog niet aangemaakt' : t.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Custom theme editor — always visible below the grid */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <h4 className="font-semibold text-gray-900 text-sm">Mijn eigen stijl genereren</h4>
                <span className="text-xs text-gray-400 ml-1">— beschrijf uw gewenste kleur en sfeer</span>
              </div>

              <div className="space-y-3">
                <textarea
                  className="input text-sm min-h-20 resize-none"
                  placeholder={`Voorbeelden:\n• "Diep paars met gouden accenten, luxe en warm"\n• "Mintgroen en wit, fris en modern"\n• "Donkerblauw als de nacht met zilver sterren, mysterieus"\n• "Warm koraalrood met zandkleurige tinten, mediterraan"`}
                  value={customDesc}
                  onChange={e => setCustomDesc(e.target.value)}
                />

                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700">Stijl:</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={customDynamic} onChange={() => setCustomDynamic(true)} className="w-4 h-4" style={{ accentColor: 'var(--brand)' }} />
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">Dynamisch</span>
                        <span className="text-gray-400 ml-1">— levendige vervagende gradiënten</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={!customDynamic} onChange={() => setCustomDynamic(false)} className="w-4 h-4" style={{ accentColor: 'var(--brand)' }} />
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">Statisch</span>
                        <span className="text-gray-400 ml-1">— strakke vlakke kleuren</span>
                      </span>
                    </label>
                  </div>
                  <button
                    onClick={handleGenerateTheme}
                    disabled={generating || !customDesc.trim()}
                    className="btn-primary text-sm disabled:opacity-50"
                  >
                    {generating ? <span className="spinner" /> : null}
                    {generating ? 'Genereren...' : 'Thema genereren'}
                  </button>
                </div>

                {genError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm px-3 py-2">{genError}</div>
                )}

                {/* Preview of generated theme */}
                {previewTheme && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl animate-fade-in-up">
                    <p className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Voorbeeld van gegenereerd thema</p>
                    <div className="flex items-start gap-4">
                      {/* Sidebar preview strip */}
                      <div className="w-12 h-28 rounded-xl flex-shrink-0 shadow-md"
                        style={{ background: previewTheme.sidebar_bg }} />
                      {/* Color swatches */}
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {['brand', 'brand_dark', 'brand_dark2', 'brand_deep', 'brand_light', 'bg_page'].map(key => (
                            previewTheme[key] && (
                              <div key={key} className="flex flex-col items-center gap-1">
                                <div className="w-8 h-8 rounded-lg border border-gray-200 shadow-sm"
                                  style={{ backgroundColor: previewTheme[key] }} />
                                <span className="text-xs text-gray-400" style={{ fontSize: 9 }}>{key.replace('_', ' ')}</span>
                              </div>
                            )
                          ))}
                        </div>
                        {/* Chat bubble preview */}
                        <div className="flex gap-2">
                          <div className="px-3 py-2 rounded-2xl text-xs text-white"
                            style={{ background: previewTheme.chat_bubble }}>Uw bericht</div>
                          <div className="px-3 py-2 rounded-2xl text-xs text-gray-700 bg-white border border-gray-200">AI antwoord</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3 mt-4">
                      <button onClick={handleApplyCustomTheme} className="btn-primary text-sm">
                        Thema toepassen
                      </button>
                      <button onClick={() => setPreviewTheme(null)} className="btn-secondary text-sm">
                        Annuleren
                      </button>
                    </div>
                  </div>
                )}

                {/* Active custom theme indicator */}
                {activeTheme === 'custom' && customTheme && !previewTheme && (
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
                    <div className="w-8 h-8 rounded-lg shadow-sm flex-shrink-0"
                      style={{ background: customTheme.sidebar_bg }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">Uw eigen thema is actief</p>
                      <p className="text-xs text-gray-400 truncate">Brand: {customTheme.brand}</p>
                    </div>
                    <button
                      onClick={() => { setTheme('ocean'); }}
                      className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Verwijderen
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
            {saving ? <span className="spinner" /> : null}
            {saving ? 'Opslaan...' : saved ? 'Instellingen opgeslagen!' : 'Instellingen opslaan'}
          </button>
        </div>
      )}

      {/* ── AI Instructies ───────────────────── */}
      {activeTab === 'ai' && (
        <div className="max-w-3xl">
          <AIInstructiesTab authFetch={authFetch} />
        </div>
      )}

      {/* ── Foto Bibliotheek ─────────────────── */}
      {activeTab === 'fotos' && (
        <div className="max-w-4xl">
          <FotoBibliotheekTab authFetch={authFetch} />
        </div>
      )}

      {/* ── E-mail Integratie ────────────────── */}
      {activeTab === 'email' && (
        <div className="max-w-3xl space-y-6">
          <div className="pro-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">E-mail integratie (IMAP)</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-gray-600">Ingeschakeld</span>
                <div className="relative">
                  <input type="checkbox" className="sr-only peer" checked={form.imap_enabled}
                    onChange={e => setForm({ ...form, imap_enabled: e.target.checked })} />
                  <div className="w-10 h-5 bg-gray-200 peer-checked:bg-blue-500 rounded-full transition-colors" />
                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                </div>
              </label>
            </div>

            {/* How it works */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p className="font-semibold mb-3">Hoe werkt de e-mail integratie?</p>
              <ol className="list-decimal list-inside space-y-2 text-xs">
                <li>Maak een map/label <strong>"FAQ"</strong> aan in uw e-mailclient</li>
                <li>Sleep klant-e-mails die u wil beantwoorden naar die map</li>
                <li>Klik op <strong>"Sync nu"</strong> op de FAQ-pagina — de e-mails verschijnen direct in TravelAIgent</li>
                <li>Klik op een e-mail in TravelAIgent en gebruik AI om het antwoord te genereren</li>
              </ol>
            </div>

            {/* Provider guides */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              {[
                {
                  name: 'Gmail',
                  icon: '📮',
                  color: '#ea4335',
                  server: 'imap.gmail.com',
                  port: 993,
                  steps: [
                    'Ga naar Google Account → Beveiliging',
                    'Schakel "2-staps verificatie" in',
                    'Maak een "App-wachtwoord" aan voor Mail',
                    'Schakel IMAP in via Gmail Instellingen → Doorsturen en POP/IMAP',
                  ]
                },
                {
                  name: 'Outlook / Hotmail',
                  icon: '📫',
                  color: '#0078d4',
                  server: 'outlook.office365.com',
                  port: 993,
                  steps: [
                    'Log in op account.microsoft.com',
                    'Ga naar Beveiliging → App-wachtwoorden',
                    'Maak een nieuw app-wachtwoord aan',
                    'IMAP staat standaard ingeschakeld',
                  ]
                },
                {
                  name: 'Andere provider',
                  icon: '📬',
                  color: '#6366f1',
                  server: 'imap.uwprovider.nl',
                  port: 993,
                  steps: [
                    'Controleer de IMAP-instellingen bij uw provider',
                    'Gebruik bij voorkeur poort 993 (SSL)',
                    'Gebruik uw gewone e-mailwachtwoord',
                    'Neem contact op met uw provider voor hulp',
                  ]
                },
              ].map(provider => (
                <div key={provider.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{provider.icon}</span>
                    <span className="font-semibold text-sm text-gray-900">{provider.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{provider.server}</code> poort {provider.port}
                  </div>
                  <ol className="list-decimal list-inside space-y-1">
                    {provider.steps.map((step, i) => (
                      <li key={i} className="text-xs text-gray-600">{step}</li>
                    ))}
                  </ol>
                  <button
                    onClick={() => setForm(f => ({ ...f, imap_host: provider.server, imap_port: provider.port }))}
                    className="mt-3 w-full py-1.5 rounded-lg text-xs font-medium border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all"
                  >
                    Gebruik {provider.name} instellingen
                  </button>
                </div>
              ))}
            </div>

            {/* IMAP fields */}
            {form.imap_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">IMAP server</label>
                  <input className="input" placeholder="imap.gmail.com" value={form.imap_host}
                    onChange={e => setForm({ ...form, imap_host: e.target.value })} />
                </div>
                <div>
                  <label className="label">Poort</label>
                  <input type="number" className="input" value={form.imap_port}
                    onChange={e => setForm({ ...form, imap_port: parseInt(e.target.value) || 993 })} />
                </div>
                <div>
                  <label className="label">E-mailadres</label>
                  <input type="email" className="input" placeholder="jan@gmail.com" value={form.imap_user}
                    onChange={e => setForm({ ...form, imap_user: e.target.value })} />
                </div>
                <div>
                  <label className="label">Wachtwoord / App-wachtwoord</label>
                  <input type="password" className="input" placeholder="••••••••••••••••" value={form.imap_password}
                    onChange={e => setForm({ ...form, imap_password: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Mapnaam om te synchroniseren (FAQ)</label>
                  <input className="input" placeholder="FAQ" value={form.imap_folder}
                    onChange={e => setForm({ ...form, imap_folder: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">Naam exact overnemen zoals in uw e-mailclient (hoofdlettergevoelig).</p>
                </div>
              </div>
            )}

            {/* Reisbescheiden / BAS settings — always visible */}
            <div className="mt-6 pt-5 border-t border-gray-100">
              <h3 className="font-semibold text-gray-800 mb-4">
                Reisbescheiden &amp; BAS-koppeling
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">BAS-nummer</label>
                  <input className="input" placeholder="bijv. 1247" value={form.bas_number}
                    onChange={e => setForm({ ...form, bas_number: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">
                    XML-bestanden worden verstuurd naar <strong>{form.bas_number || 'uw nummer'}@bas.nl</strong>
                  </p>
                </div>
                <div>
                  <label className="label">Mapnaam Reisbescheiden (IMAP)</label>
                  <input className="input" placeholder="Reisbescheiden" value={form.reisbescheiden_folder}
                    onChange={e => setForm({ ...form, reisbescheiden_folder: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">Map in uw mailbox waar Corendon bevestigingen automatisch worden opgepikt.</p>
                </div>
                <div>
                  <label className="label">Mapnaam Geschiedenis (IMAP)</label>
                  <input className="input" placeholder="History" value={form.history_folder}
                    onChange={e => setForm({ ...form, history_folder: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">Verwerkte e-mails (FAQ beantwoord/verwijderd, Reisbescheiden vergeleken) worden automatisch naar deze map verplaatst.</p>
                </div>
                <div>
                  <label className="label">SMTP-server (uitgaande mail naar BAS)</label>
                  <input className="input" placeholder="Leeg = afgeleid van IMAP-server" value={form.smtp_host}
                    onChange={e => setForm({ ...form, smtp_host: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">Leeg laten: wordt automatisch afgeleid (imap.x.nl → smtp.x.nl).</p>
                </div>
                <div>
                  <label className="label">SMTP-poort</label>
                  <input className="input" type="number" placeholder="587" value={form.smtp_port}
                    onChange={e => setForm({ ...form, smtp_port: parseInt(e.target.value) || 587 })} />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                {saving ? <span className="spinner" /> : null}
                {saving ? 'Opslaan...' : saved ? 'Opgeslagen!' : 'E-mailinstellingen opslaan'}
              </button>
            </div>
          </div>

          {/* Automatische afspraak e-mails */}
          <div className="pro-card p-5">
            <h2 className="font-semibold text-gray-900 mb-1">Automatische afspraak e-mails</h2>
            <p className="text-xs text-gray-500 mb-4">
              Deze e-mails worden automatisch verstuurd via uw SMTP-verbinding — zonder AI.
              De <strong>bevestigingsmail</strong> wordt direct gestuurd wanneer u een afspraak bevestigt.
              De <strong>herinneringsmail</strong> wordt 1 dag voor de afspraak automatisch verstuurd.
              Laat een veld leeg om de standaard tekst te gebruiken.
            </p>

            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <strong>Beschikbare variabelen:</strong>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{client_name}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{appointment_date}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{appointment_time}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{appointment_type}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{location_info}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{agency_name}}'}</code>{' '}
              <code className="bg-amber-100 px-1 rounded">{'{{signoff}}'}</code>
            </div>

            <div className="space-y-5">
              <div>
                <label className="label">Bevestigingsmail template</label>
                <p className="text-xs text-gray-400 mb-1">Wordt verstuurd zodra u een afspraak bevestigt.</p>
                <textarea
                  className="input font-mono text-xs min-h-48"
                  placeholder={`Beste {{client_name}},\n\nUw afspraak is bevestigd!\n\n📅 Datum: {{appointment_date}}\n🕐 Tijd: {{appointment_time}}\n📍 Type: {{appointment_type}}\n{{location_info}}\n\n{{signoff}}`}
                  value={form.appointment_confirmation_template}
                  onChange={e => setForm(f => ({ ...f, appointment_confirmation_template: e.target.value }))}
                />
              </div>
              <div>
                <label className="label">Herinneringsmail template</label>
                <p className="text-xs text-gray-400 mb-1">Wordt automatisch verstuurd 1 dag voor de afspraak.</p>
                <textarea
                  className="input font-mono text-xs min-h-48"
                  placeholder={`Beste {{client_name}},\n\nDit is een herinnering dat uw afspraak morgen plaatsvindt.\n\n📅 Datum: {{appointment_date}}\n🕐 Tijd: {{appointment_time}}\n\nTot morgen!\n\n{{signoff}}`}
                  value={form.appointment_reminder_template}
                  onChange={e => setForm(f => ({ ...f, appointment_reminder_template: e.target.value }))}
                />
              </div>
            </div>

            <div className="mt-4">
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                {saving ? <span className="spinner" /> : null}
                {saving ? 'Opslaan...' : saved ? 'Opgeslagen!' : 'Templates opslaan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Kennisbank AI ───────────────────────── */}
      {activeTab === 'kennisbank' && (
        <>
          <KennisbankTab form={form} setForm={setForm} />
          <div className="max-w-2xl mt-6">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
              {saving && <span className="spinner" />}
              {saving ? 'Opslaan...' : saved ? 'Opgeslagen!' : 'Instellingen opslaan'}
            </button>
          </div>
        </>
      )}
    </div>
    </div>
  );
}
